import React, { useState } from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Card from "@mui/material/Card";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import { green, red } from "@mui/material/colors";
import axios from "axios";
import i18n from "../../i18n";

/**
 * The offer to queue on a full category's reserve list, for a registration turned away on
 * the limit alone (`reserveAvailable` in the rejection). The server checks everything again,
 * so the answer may also be that a place has opened after all.
 */
const ReserveOffer = ({ category, uuid, api, selectedDances, t }) => {
    const [status, setStatus] = useState("offer");
    const [position, setPosition] = useState(null);

    const join = () => {
        setStatus("loading");
        axios
            .put(`${api.joinReserveAnonymously}${uuid}`, {
                category: category.id,
                selectedDances: selectedDances || {},
                locale: i18n.language
            })
            .then(response => {
                const data = response.data || {};
                if (data.status === "reserve") {
                    setPosition(data.position);
                    setStatus("reserve");
                } else if (data.status === "not_full") {
                    setStatus("not_full");
                } else {
                    setStatus("error");
                }
            })
            .catch(() => setStatus("error"));
    };

    if (status === "reserve") {
        return (
            <Alert severity="warning" icon={<HourglassTopIcon />}>
                {`${t("reg.reserve.joined")} — ${t("reg.reserve.position")}: ${position}`}
            </Alert>
        );
    }
    if (status === "not_full") {
        return <Alert severity="info">{t("reg.reserve.not_full")}</Alert>;
    }

    return (
        <Alert severity={status === "error" ? "error" : "info"} icon={<HourglassTopIcon />}>
            {status === "error" ? t("reg.reserve.failed") : t("reg.reserve.disclaimer")}
            <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="warning" disabled={status === "loading"} onClick={join}>
                    {t("reg.reserve.join")}
                </Button>
            </Box>
            {status === "loading" && <LinearProgress sx={{ mt: 1 }} />}
        </Alert>
    );
};

const CategoriesRegistrationStatus = ({ entries, rejected, t, uuid, api, selectedDances }) => {
    const canJoinReserve = !!(uuid && api && api.joinReserveAnonymously);

    return (
        <Box>
            {entries?.length > 0 && (
                <Card sx={{ minWidth: 275 }}>
                    <CardContent>
                        <Typography variant="h6" component="div">
                            {t("reg.status.accepted")}
                        </Typography>
                        <List>
                            {entries.map(c => {
                                return (
                                    <ListItem key={c.id} disablePadding>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: green[500], width: 24, height: 24 }}>
                                                <CheckIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText id={`cat-lab-${c.id}`} primary={c.name} />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </CardContent>
                </Card>
            )}
            {rejected?.length > 0 && (
                <Card sx={{ minWidth: 275 }}>
                    <CardContent>
                        <Typography variant="h6" component="div">
                            {t("reg.status.rejected")}
                        </Typography>
                        <List>
                            {rejected.map(({ 0: c, 1: reason }) => {
                               // console.log(reason);
                                const reasonTxt =
                                    reason.regOpen === false
                                        ? t("reg.status.reason.reg_open")
                                        : reason.age === false
                                        ? t("reg.status.reason.age")
                                        : reason.class === false
                                        ? t("reg.status.reason.class")
                                        : reason.licence === false
                                        ? t("reg.status.reason.licence")
                                        : reason.style === false
                                        ? t("reg.status.reason.style")
                                        : reason.limits === false
                                        ? t("reg.status.reason.limits")
                                        : reason.count === false
                                        ? t("reg.status.reason.count")
                                        : reason.codeOk === false
                                        ? t("reg.status.reason.code")
                                        : reason.wdsf === false
                                        ? `${t("reg.status.reason.wdsf")}${reason.wdsfMessage ? ": " + reason.wdsfMessage : ""}`
                                        : reason.wdsfId === false
                                        ? t("reg.status.reason.wdsf_id")
                                        : reason.wdsfCategoryId === false
                                        ? t("reg.status.reason.wdsf_category_id")
                                        : reason.otherComps === false
                                        ? t("reg.status.reason.otherComps")
                                        : t("reg.status.reason.unknown");

                                return (
                                    <React.Fragment key={c.id}>
                                        <ListItem disablePadding>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: red[500], width: 24, height: 24 }}>
                                                    <ClearIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText id={`cat-lab-${c.id}`} primary={c.name} secondary={reasonTxt} />
                                        </ListItem>
                                        {reason.reserveAvailable === true && canJoinReserve && (
                                            <Box sx={{ pl: 7, pb: 1 }}>
                                                <ReserveOffer category={c} uuid={uuid} api={api} selectedDances={selectedDances} t={t} />
                                            </Box>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default CategoriesRegistrationStatus;
